import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, X, Zap, Music, Crown, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl } from "../config/api";

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/subscriptions/plans`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      toast.error("Failed to load pricing plans");
    }
  }, []);

  const fetchUserSubscription = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${getBackendUrl()}/api/subscriptions/user/${user.id}`);
      const data = await response.json();
      setUserSubscription(data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlans();
    fetchUserSubscription();
  }, [fetchPlans, fetchUserSubscription]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }

    if (planId === "free") {
      toast.info("You're already on the Free plan!");
      return;
    }

    setSubscribing(planId);

    try {
      const response = await fetch(`${getBackendUrl()}/api/subscriptions/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          plan_id: planId,
          billing_cycle: billingCycle,
          return_url: `${window.location.origin}/pricing?subscription_success=true`,
          cancel_url: `${window.location.origin}/pricing?subscription_cancelled=true`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to create subscription");
      }

      if (data.mock_mode) {
        toast.success(`Subscribed to ${data.plan_name}! (Demo Mode)`);
        fetchUserSubscription();
      } else if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.success(`Subscribed to ${data.plan_name}!`);
        fetchUserSubscription();
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error.message || "Failed to subscribe");
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription?")) {
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          reason: "User requested cancellation"
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to cancel subscription");
      }

      toast.success("Subscription cancelled successfully");
      fetchUserSubscription();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(error.message || "Failed to cancel subscription");
    }
  };

  // Check for return from PayPal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription_success") === "true") {
      toast.success("Subscription activated successfully!");
      window.history.replaceState({}, "", "/pricing");
      fetchUserSubscription();
    } else if (params.get("subscription_cancelled") === "true") {
      toast.info("Subscription process was cancelled");
      window.history.replaceState({}, "", "/pricing");
    }
  }, [fetchUserSubscription]);

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "free":
        return <Music className="w-8 h-8" />;
      case "pro":
        return <Zap className="w-8 h-8" />;
      case "studio":
        return <Crown className="w-8 h-8" />;
      default:
        return <Music className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case "free":
        return "from-gray-500 to-gray-600";
      case "pro":
        return "from-indigo-500 to-purple-600";
      case "studio":
        return "from-amber-500 to-orange-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getPrice = (plan) => {
    if (plan.price_monthly === 0) return "Free";
    return billingCycle === "monthly" 
      ? `$${plan.price_monthly}` 
      : `$${plan.price_yearly}`;
  };

  const getSavings = (plan) => {
    if (plan.price_monthly === 0) return null;
    const monthlyTotal = plan.price_monthly * 12;
    const savings = monthlyTotal - plan.price_yearly;
    return savings.toFixed(2);
  };

  const isCurrentPlan = (planId) => {
    if (!userSubscription) return planId === "free";
    return userSubscription.plan_id === planId && !userSubscription.is_free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
            String Sauce
          </h1>
          {user ? (
            <div className="text-sm text-gray-400">
              Current Plan: <span className="text-white font-medium">{userSubscription?.plan_name || "Free"}</span>
            </div>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              size="sm"
              data-testid="login-button"
            >
              Sign In
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Choose Your <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">Creative Journey</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            From hobbyist to professional, we have a plan that fits your music production needs.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white/5 rounded-full p-1.5" data-testid="billing-toggle">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-indigo-500 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid="monthly-toggle"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "yearly"
                  ? "bg-indigo-500 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid="yearly-toggle"
            >
              Yearly
              <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                Save 15%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border ${
                plan.popular
                  ? "border-indigo-500/50 bg-indigo-500/5"
                  : "border-white/10 bg-white/5"
              } p-6 flex flex-col`}
              data-testid={`plan-card-${plan.id}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${getPlanColor(plan.id)} mb-4`}>
                  {getPlanIcon(plan.id)}
                </div>
                <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                <p className="text-gray-400 text-sm">{plan.tagline}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">{getPrice(plan)}</span>
                  {plan.price_monthly > 0 && (
                    <span className="text-gray-400">/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                  )}
                </div>
                {billingCycle === "yearly" && getSavings(plan) && (
                  <p className="text-green-400 text-sm mt-1">
                    Save ${getSavings(plan)} per year
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6 flex-1">
                {plan.feature_list.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
                {plan.limitations?.map((limitation, idx) => (
                  <li key={`limit-${idx}`} className="flex items-start gap-3 text-sm">
                    <X className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-500">{limitation}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <div className="mt-auto">
                {isCurrentPlan(plan.id) ? (
                  <div className="space-y-2">
                    <Button
                      disabled
                      className="w-full bg-green-500/20 text-green-400 cursor-default"
                      data-testid={`current-plan-${plan.id}`}
                    >
                      Current Plan
                    </Button>
                    {plan.id !== "free" && (
                      <button
                        onClick={handleCancelSubscription}
                        className="w-full text-sm text-gray-500 hover:text-red-400 transition-colors"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    className={`w-full ${
                      plan.popular
                        ? "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
                        : plan.id === "studio"
                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                        : "bg-white/10 hover:bg-white/20"
                    }`}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {subscribing === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : plan.id === "free" ? (
                      "Get Started Free"
                    ) : userSubscription?.plan_id === "free" || !userSubscription ? (
                      "Upgrade Now"
                    ) : (
                      "Switch Plan"
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-24">
          <h3 className="text-2xl font-bold text-center mb-8">Feature Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full max-w-4xl mx-auto" data-testid="feature-comparison-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-gray-400 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-gray-400 font-medium">Free</th>
                  <th className="text-center py-4 px-4 text-indigo-400 font-medium">Pro</th>
                  <th className="text-center py-4 px-4 text-amber-400 font-medium">Studio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <FeatureRow feature="Tracks per project" free="4" pro="8" studio="Unlimited" />
                <FeatureRow feature="Projects" free="3" pro="Unlimited" studio="Unlimited" />
                <FeatureRow feature="Cloud storage" free="2 GB" pro="10 GB" studio="50 GB" />
                <FeatureRow feature="Export formats" free="WAV" pro="WAV, MP3" studio="WAV, MP3" />
                <FeatureRow feature="Amp models" free="2" pro="4" studio="4" />
                <FeatureRow feature="Pedal effects" free="3" pro="9" studio="9" />
                <FeatureRow feature="Preset downloads" free={false} pro="10/month" studio="Unlimited" />
                <FeatureRow feature="MIDI tools" free={false} pro={true} studio={true} />
                <FeatureRow feature="Sell on marketplace" free={false} pro={false} studio={true} />
                <FeatureRow feature="Automation lanes" free={false} pro={false} studio={true} />
                <FeatureRow feature="Piano roll editor" free={false} pro={false} studio={true} />
                <FeatureRow feature="Analytics dashboard" free={false} pro={false} studio={true} />
                <FeatureRow feature="Undo history" free="20" pro="50" studio="100" />
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-24 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <FaqItem
              question="Can I cancel my subscription anytime?"
              answer="Yes! You can cancel your subscription at any time. You'll continue to have access to your paid features until the end of your billing period."
            />
            <FaqItem
              question="What payment methods do you accept?"
              answer="We accept PayPal for all subscription payments. PayPal supports credit cards, debit cards, and PayPal balance."
            />
            <FaqItem
              question="Can I upgrade or downgrade my plan?"
              answer="Absolutely! You can change your plan at any time. When upgrading, you'll get immediate access to new features. When downgrading, the change takes effect at the end of your billing period."
            />
            <FaqItem
              question="What happens to my projects if I downgrade?"
              answer="Your projects and presets are safe. However, you may need to reduce track counts or projects to match your new plan's limits before creating new content."
            />
            <FaqItem
              question="Is there a free trial?"
              answer="Our Free tier lets you explore String Sauce with no time limit. When you're ready for more features, upgrade to Pro or Studio."
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-24 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>String Sauce © 2026. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureRow({ feature, free, pro, studio }) {
  const renderValue = (value) => {
    if (value === true) {
      return <Check className="w-5 h-5 text-green-400 mx-auto" />;
    }
    if (value === false) {
      return <X className="w-5 h-5 text-gray-600 mx-auto" />;
    }
    return <span className="text-gray-300">{value}</span>;
  };

  return (
    <tr>
      <td className="py-3 px-4 text-gray-300">{feature}</td>
      <td className="py-3 px-4 text-center">{renderValue(free)}</td>
      <td className="py-3 px-4 text-center">{renderValue(pro)}</td>
      <td className="py-3 px-4 text-center">{renderValue(studio)}</td>
    </tr>
  );
}

function FaqItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <span className="font-medium">{question}</span>
        <span className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-6 pb-4 text-gray-400">
          {answer}
        </div>
      )}
    </div>
  );
}
